import axios from 'axios';
import { Auth } from 'aws-amplify';
import { ACCESS_TOKEN_LOCAL_STORAGE_KEY, SUCCESSFUL_HTTP_STATUSES } from 'constant';
import { checkUserAuth } from 'utils';

import { runtimeConfig } from 'constant/runtime';

export const baseURL = runtimeConfig.REACT_APP_API_BASE_URL;

const Api = axios.create({
  baseURL,
});

Api.defaults.headers.post['Content-Type'] = 'application/json';

const syncTokenToLocalStorage = (token: string, timeout: number) => {
  const tokenLocalStorageData = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  let tokenData = null;

  try {
    tokenData = tokenLocalStorageData ? JSON.parse(tokenLocalStorageData) : null;
  } catch (error) {
    tokenData = null;
  }

  if (!tokenData) return;

  localStorage.setItem(
    ACCESS_TOKEN_LOCAL_STORAGE_KEY,
    JSON.stringify({
      ...tokenData,
      token,
      timeout: Math.max(timeout, 0),
      createdOn: Date.now(),
    }),
  );
};

export const getTokenForRequest = async () => {
  try {
    const session = await Auth.currentSession();
    const idToken = session?.getIdToken();
    const freshToken = idToken?.getJwtToken() || '';

    if (freshToken) {
      const expiresAtMs = idToken?.payload?.exp ? Number(idToken.payload.exp) * 1000 : Date.now();
      syncTokenToLocalStorage(freshToken, expiresAtMs - Date.now());
      return freshToken;
    }
  } catch (error) {
    // No active session or refresh failed; fallback to stored token.
  }

  return checkUserAuth();
};

Api.interceptors.request.use(async (req) => {
  const token = await getTokenForRequest();

  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }

  return req;
});

Api.interceptors.response.use(
  (response) => {
    const isSuccessStatus = SUCCESSFUL_HTTP_STATUSES.includes(response.status);

    if (!isSuccessStatus) {
      return Promise.reject(response.data.message);
    }

    return response;
  },
  async (error) => {
    return Promise.reject(error.message);
  },
);

export default Api;
